import { z } from "zod";

// =============================================================================
// Laufzeit-Konfiguration der Web-App
// =============================================================================
// Zwei Quellen, je nach Art des Starts:
//   - Dev-Server `dev:aws`: Vite-Variablen (VITE_…) aus `.env.aws`, beim Start
//     eingesetzt (Entscheidung 38).
//   - Produktions-Build (S3 + CloudFront): `/config.json`, die der EdgeStack je
//     Stage neben die App schreibt. So gilt EIN Build-Artefakt für dev, staging
//     und prod, statt je Stage neu zu bauen.
// Alles hier ist öffentlich (Pool-ID, Client-ID, Domain), nichts davon geheim.

// Anmeldeart (Entscheidung 38):
//   "local"   – nur im Dev-Server ohne --mode aws: Test-Token vom lokalen Dev-Server
//   "cognito" – dev:aws und jeder Produktions-Build
// `import.meta.env.DEV` ist im Produktions-Build die Konstante `false`. Vite
// entfernt damit den gesamten Zweig für den lokalen Login aus dem Bundle.
export const authMode: "local" | "cognito" =
  import.meta.env.DEV && import.meta.env.MODE !== "aws" ? "local" : "cognito";

const cognitoSchema = z.object({
  region: z.literal("eu-central-1"),
  userPoolId: z.string().regex(/^eu-central-1_[A-Za-z0-9]+$/, "userPoolId ungültig"),
  clientId: z.string().min(1, "clientId fehlt"),
  // Managed-Login-Domain, z. B. https://ortura-dev.auth.eu-central-1.amazoncognito.com
  domain: z.url("domain fehlt oder ist keine URL").transform((value) => value.replace(/\/+$/, "")),
});
export type CognitoConfig = z.infer<typeof cognitoSchema>;

// Aufbau von /config.json. Muss zu dem passen, was `infra/lib/edge-stack.ts`
// schreibt; ein Feld mehr oder weniger fällt hier sofort mit klarer Meldung auf.
const runtimeConfigSchema = z.object({
  cognito: cognitoSchema.omit({ region: true }),
});

// Prüft den Inhalt von /config.json. Eigene Funktion, damit sie ohne Browser
// testbar ist (`unknown`: wir vertrauen dem JSON erst nach der Prüfung).
export function parseRuntimeConfig(json: unknown): CognitoConfig {
  const { cognito } = runtimeConfigSchema.parse(json);
  return cognitoSchema.parse({ region: "eu-central-1", ...cognito });
}

// Liest und prüft die Cognito-Werte. Fehler sofort und mit klarer Meldung,
// statt später mit einem kryptischen OIDC-Fehler. `async`, weil der
// Produktions-Build die Datei erst per fetch() holen muss.
export async function loadCognitoConfig(): Promise<CognitoConfig> {
  if (import.meta.env.DEV) {
    return cognitoSchema.parse({
      region: "eu-central-1",
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
      domain: import.meta.env.VITE_COGNITO_DOMAIN,
    });
  }
  // `no-store`: nach einem Deploy sofort die neuen Werte, nie eine alte Kopie
  // aus dem Browser-Cache.
  const response = await fetch("/config.json", { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`/config.json nicht ladbar (HTTP ${response.status})`);
  }
  return parseRuntimeConfig(await response.json());
}
